// The landing page, rebuilt to "Landing - Field Notes.dc.html".
//
// Two-column hero: the composer on the left, a taped photo collage on the
// right. Then the three numbered pillars in a ruled row, and the destination
// cards on paper-warm.

import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  trackTripStarted,
  trackTripCreated,
  trackComposerTripStarted,
} from "@/lib/analytics";
import { useJourneys } from "@/context/JourneyContext";
import TripComposer, { type PresetApplication } from "@/components/TripComposer";
import { PRESETS, type TripPreset } from "@/lib/presets";
import { getCoverImage } from "@/lib/trip-helpers";

const PILLARS = [
  {
    no: "01",
    title: "Verified places",
    body: "Every restaurant, bar and stay cross-checked on Google Places. No closed cafés, no ghosts.",
  },
  {
    no: "02",
    title: "Creator-sourced",
    body: "Picks drawn from creators you already trust, not top-ten listicles.",
  },
  {
    no: "03",
    title: "One living plan",
    body: "Maps, hours, bookings and your own notes: one page that travels with you.",
  },
];

// Each card sits at its own slight angle with a strip of tape, so the row
// reads as photographs laid on a page rather than a grid of tiles.
const CARD_ANGLES = [
  { rot: -1.2, tape: 1.6 },
  { rot: 1, tape: -2 },
  { rot: -0.8, tape: 2 },
  { rot: 1.4, tape: -1.4 },
];

const Index = () => {
  const navigate = useNavigate();
  const { createEmptyJourney } = useJourneys();

  const [presetApplication, setPresetApplication] =
    useState<PresetApplication | null>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const presetTapCount = useRef(0);

  const startJourney = (initialPrompt: string, presetId?: string | null) => {
    const trimmed = initialPrompt.trim();
    const journey = createEmptyJourney();
    trackTripStarted("landing", !!trimmed);
    trackTripCreated("landing");
    if (trimmed) {
      navigate(`/journey/${journey.id}`, {
        state: { initialPrompt: trimmed, presetId: presetId ?? undefined },
      });
    } else {
      // Completely empty composer: land on a quiet empty planner, no
      // auto-sent message, no API cost.
      navigate(`/journey/${journey.id}`);
    }
  };

  const handleComposerStart = (
    prompt: string,
    meta: { vibeCount: number; presetId: string | null; structured: boolean }
  ) => {
    if (prompt.trim()) {
      trackComposerTripStarted({
        vibeCount: meta.vibeCount,
        usedPreset: !!meta.presetId,
        structured: meta.structured,
      });
    }
    startJourney(prompt, meta.presetId);
  };

  // A card prefills the composer rather than starting a trip: the composer is
  // where dates and moods get refined. The tap counter keys the application so
  // re-tapping the same card re-applies it.
  const handleDestinationCard = (preset: TripPreset) => {
    presetTapCount.current += 1;
    setPresetApplication({ preset, key: presetTapCount.current });
    composerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      {/* Hero */}
      <section className="mx-auto max-w-[1200px] px-5 md:px-12 pt-10 md:pt-16 pb-12 md:pb-[72px] grid gap-10 lg:gap-16 lg:grid-cols-[1fr_400px] items-start">
        <div ref={composerRef} className="min-w-0">
          <div className="font-sans text-xs font-semibold uppercase tracking-[0.22em] text-ink-muted mb-[22px]">
            The travel concierge · No. 01
          </div>

          <h1 className="font-serif text-[42px] md:text-[68px] leading-[1.08] md:leading-[1.04] tracking-[-0.01em] mb-4 md:mb-[26px]">
            Where are you
            <br />
            <span className="relative inline-block italic">
              off to
              {/* Hand-drawn ring rather than a straight underline. */}
              <svg
                viewBox="0 0 240 96"
                fill="none"
                aria-hidden="true"
                className="absolute -top-2.5 -left-[18px] w-[calc(100%+36px)] h-[calc(100%+20px)] md:-top-3.5 md:-left-[26px] md:w-[calc(100%+52px)] md:h-[calc(100%+28px)] pointer-events-none"
              >
                <path
                  d="M18 52 C10 22, 92 6, 152 12 C214 18, 236 42, 220 64 C202 88, 66 94, 30 74 C12 64, 14 52, 34 42"
                  stroke="var(--fn-accent)"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </span>{" "}
            next?
          </h1>

          <p className="font-serif text-[15.5px] md:text-[18px] leading-[1.6] text-ink-muted max-w-[500px] mb-6 md:mb-9">
            Tell the concierge what you're into. It writes a day-by-day plan
            from places that actually check out.
          </p>

          <TripComposer application={presetApplication} onStart={handleComposerStart} />
        </div>

        {/* Photo collage */}
        <div className="relative hidden lg:block" aria-hidden="true">
          <div className="relative rotate-[1.2deg]">
            <img
              src={getCoverImage("Bangkok")}
              alt=""
              className="w-full h-[400px] object-cover block border border-rule"
            />
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 rotate-[-2deg] bg-tape w-24 h-[26px] shadow-[0_1px_2px_rgba(0,0,0,0.08)]" />
            <div className="bg-white border border-rule border-t-0 px-4 py-3 flex justify-between items-center gap-2.5">
              <span className="font-serif italic text-base">Bangkok, Thailand</span>
              <span className="font-sans text-[10.5px] font-bold uppercase tracking-[0.14em] text-accent-text whitespace-nowrap">
                18 verified picks
              </span>
            </div>
          </div>

          <div className="absolute -bottom-2 -left-10 rotate-[-2.4deg] bg-white border border-rule p-2.5 pb-3 w-[170px] shadow-[0_10px_24px_-10px_rgba(var(--fn-shadow-rgb),0.25)]">
            <img
              src={getCoverImage("Kyoto")}
              alt=""
              className="w-full h-28 object-cover block"
            />
            <div className="hand text-[18px] text-ink mt-2 text-center">
              Kyoto in November?
            </div>
          </div>

          <svg
            viewBox="0 0 120 90"
            fill="none"
            className="absolute bottom-14 left-[104px] w-[104px] pointer-events-none"
          >
            <path d="M108 10 C70 18, 40 34, 22 66" stroke="var(--fn-ink)" strokeWidth="2.2" strokeLinecap="round" strokeDasharray="1 7" />
            <path d="M32 52 L20 70 L40 68" stroke="var(--fn-ink)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </section>

      {/* Three pillars, divided by vertical rules */}
      <section className="mx-auto max-w-[1200px] px-5 md:px-12">
        <div className="grid md:grid-cols-3 border-t border-rule">
          {PILLARS.map((p, i) => (
            <div
              key={p.no}
              className={`py-7 md:px-10 ${i < 2 ? "md:border-r border-rule" : ""} ${i > 0 ? "border-t md:border-t-0 border-rule" : ""}`}
            >
              <div className="font-serif italic text-[15px] text-ink-faint mb-2">{p.no}</div>
              <div className="font-sans text-[13px] font-bold uppercase tracking-[0.12em] mb-1.5">
                {p.title}
              </div>
              <p className="font-serif text-[15px] leading-[1.55] text-ink-muted">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Destination cards */}
      <section className="border-t border-rule bg-paper-warm">
        <div className="mx-auto max-w-[1200px] px-5 md:px-12 py-14 md:pt-14 md:pb-16">
          <div className="flex items-baseline justify-between gap-5 mb-7 flex-wrap">
            <h2 className="font-serif text-[26px] md:text-[32px]">Start somewhere inspiring</h2>
            <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-muted">
              Tap a city to prefill the composer
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PRESETS.map((preset, i) => {
              const angle = CARD_ANGLES[i % CARD_ANGLES.length];
              return (
                <button
                  key={preset.id}
                  onClick={() => handleDestinationCard(preset)}
                  className="group block w-full text-left"
                >
                  <span
                    className="relative block transition-transform duration-200 group-hover:rotate-0"
                    style={{ transform: `rotate(${angle.rot}deg)` }}
                  >
                    <span
                      role="img"
                      aria-label={preset.destination}
                      className="block w-full h-[220px] bg-cover bg-center bg-map border border-rule box-border"
                      style={{ backgroundImage: `url(${getCoverImage(preset.destination)})` }}
                    />
                    <span
                      aria-hidden="true"
                      className="absolute -top-[9px] left-1/2 bg-tape w-16 h-5 block shadow-[0_1px_2px_rgba(0,0,0,0.08)]"
                      style={{ transform: `translateX(-50%) rotate(${angle.tape}deg)` }}
                    />
                  </span>
                  <span className="flex justify-between items-baseline mt-3 gap-2">
                    <span className="font-serif text-[19px] text-ink group-hover:text-accent-text transition-colors">
                      {preset.destination}
                    </span>
                    {preset.picksCount ? (
                      <span className="font-sans text-[9.5px] font-bold uppercase tracking-[0.12em] text-accent-text whitespace-nowrap">
                        {preset.picksCount} verified picks
                      </span>
                    ) : null}
                  </span>
                  <span className="block text-[12.5px] text-ink-muted mt-0.5">{preset.tagline}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default Index;
